/**
 * Compact property tables behind the interactive periodic table.
 *
 * Names and atomic weights come from the shared chemistry core
 * (`@/lib/tool-kit/chemistry/elements`, IUPAC/CIAAW 2024). Everything here is indexed by
 * atomic number Z (entry 0 is Z = 1) so that the whole 118-element set stays a few
 * kilobytes in the client bundle.
 *
 * ## Sources
 *
 * - **Electronegativity** (Pauling scale), **category** and **discovery year**: the
 *   PubChem Periodic Table (NCBI, https://pubchem.ncbi.nlm.nih.gov/ptable/), whose Pauling
 *   values follow the CRC Handbook of Chemistry and Physics. `null` means no accepted value
 *   (He, Ne, Ar, Rn, Pm, Eu, Tb, Yb and every element from Rf upward). The values for
 *   Es–Lr (1.3) and several other actinides are estimates, as in the CRC Handbook.
 * - **Discovery year**: PubChem's `YearDiscovered`, where `0` marks the elements known
 *   since antiquity. Three PubChem entries date the *compound*, not the element — fluorine
 *   (1670, fluorspar), calcium and aluminium ("Ancient", lime and alum) — and are replaced
 *   here by the year the element itself was first isolated: F 1886 (Moissan), Ca 1808
 *   (Davy), Al 1825 (Ørsted). Dates for elements known for centuries are conventions.
 * - **Ground-state electron configurations**: the Madelung (n + ℓ) filling order, with the
 *   measured exceptions of the NIST Atomic Spectra Database ground levels (and CRC
 *   Handbook, "Electron Configuration of Neutral Atoms in the Ground State") listed in
 *   `CONFIG_EXCEPTIONS`. Rf (104) to Og (118) have no measured configuration; theirs are
 *   the simple Madelung predictions and are flagged as predicted.
 * - **Group layout**: IUPAC's 2021 provisional recommendation that group 3 holds Sc, Y, Lu
 *   and Lr, so La–Yb and Ac–No form the 14-element f-block rows.
 */

export type CategoryId =
  | "alkali"
  | "alkaline"
  | "transition"
  | "post"
  | "metalloid"
  | "nonmetal"
  | "halogen"
  | "noble"
  | "lanthanide"
  | "actinide";

/** Two-letter marker printed in every cell so colour is never the only cue. */
export const CATEGORY_CODES: Record<CategoryId, string> = {
  alkali: "AM",
  alkaline: "AE",
  transition: "TM",
  post: "PT",
  metalloid: "MD",
  nonmetal: "NM",
  halogen: "HL",
  noble: "NG",
  lanthanide: "LN",
  actinide: "AC",
};

const CATEGORY_RUNS: readonly (readonly [number, CategoryId])[] = [
  [1, "nonmetal"],
  [2, "noble"],
  [3, "alkali"],
  [4, "alkaline"],
  [5, "metalloid"],
  [6, "nonmetal"],
  [9, "halogen"],
  [10, "noble"],
  [11, "alkali"],
  [12, "alkaline"],
  [13, "post"],
  [14, "metalloid"],
  [15, "nonmetal"],
  [17, "halogen"],
  [18, "noble"],
  [19, "alkali"],
  [20, "alkaline"],
  [21, "transition"],
  [31, "post"],
  [32, "metalloid"],
  [34, "nonmetal"],
  [35, "halogen"],
  [36, "noble"],
  [37, "alkali"],
  [38, "alkaline"],
  [39, "transition"],
  [49, "post"],
  [51, "metalloid"],
  [53, "halogen"],
  [54, "noble"],
  [55, "alkali"],
  [56, "alkaline"],
  [57, "lanthanide"],
  [72, "transition"],
  [81, "post"],
  [84, "metalloid"],
  [85, "halogen"],
  [86, "noble"],
  [87, "alkali"],
  [88, "alkaline"],
  [89, "actinide"],
  [104, "transition"],
  [113, "post"],
  [117, "halogen"],
  [118, "noble"],
];

/** Category of each element: the run that starts at or before Z and is nearest to it. */
export const CATEGORIES: readonly CategoryId[] = (() => {
  const out: CategoryId[] = [];
  let current: CategoryId = "nonmetal";
  let next = 0;
  for (let z = 1; z <= 118; z += 1) {
    while (next < CATEGORY_RUNS.length && CATEGORY_RUNS[next][0] <= z) {
      current = CATEGORY_RUNS[next][1];
      next += 1;
    }
    out.push(current);
  }
  return out;
})();

/** Pauling electronegativity by Z; `null` where there is no accepted value. */
export const ELECTRONEGATIVITY: readonly (number | null)[] = [
  2.2,
  null,
  0.98,
  1.57,
  2.04,
  2.55,
  3.04,
  3.44,
  3.98,
  null,
  0.93,
  1.31,
  1.61,
  1.9,
  2.19,
  2.58,
  3.16,
  null,
  0.82,
  1,
  1.36,
  1.54,
  1.63,
  1.66,
  1.55,
  1.83,
  1.88,
  1.91,
  1.9,
  1.65,
  1.81,
  2.01,
  2.18,
  2.55,
  2.96,
  3,
  0.82,
  0.95,
  1.22,
  1.33,
  1.6,
  2.16,
  1.9,
  2.2,
  2.28,
  2.2,
  1.93,
  1.69,
  1.78,
  1.96,
  2.05,
  2.1,
  2.66,
  2.6,
  0.79,
  0.89,
  1.1,
  1.12,
  1.13,
  1.14,
  null,
  1.17,
  null,
  1.2,
  null,
  1.22,
  1.23,
  1.24,
  1.25,
  null,
  1.27,
  1.3,
  1.5,
  2.36,
  1.9,
  2.2,
  2.2,
  2.28,
  2.54,
  2,
  1.62,
  2.33,
  2.02,
  2,
  2.2,
  null,
  0.7,
  0.9,
  1.1,
  1.3,
  1.5,
  1.38,
  1.36,
  1.28,
  1.3,
  1.3,
  1.3,
  1.3,
  1.3,
  1.3,
  1.3,
  1.3,
  1.3,
  null,
  null,
  null,
  null,
  null,
  null,
  null,
  null,
  null,
  null,
  null,
  null,
  null,
  null,
  null,
];

/** Year of discovery by Z; `0` means known since antiquity. */
export const DISCOVERY_YEARS: readonly number[] = [
  1766, 1868, 1817, 1798, 1808, 0, 1772, 1774, 1886, 1898, 1807, 1808, 1825,
  1854, 1669, 0, 1774, 1894, 1807, 1808, 1879, 1791, 1801, 1797, 1774, 0, 1735,
  1751, 0, 1746, 1875, 1886, 0, 1817, 1826, 1898, 1861, 1790, 1794, 1789, 1801,
  1778, 1937, 1827, 1803, 1803, 0, 1817, 1863, 0, 0, 1782, 1811, 1898, 1860,
  1808, 1839, 1803, 1885, 1885, 1945, 1879, 1901, 1880, 1843, 1886, 1878, 1843,
  1879, 1878, 1907, 1923, 1802, 1783, 1925, 1803, 1803, 1735, 0, 0, 1861, 0,
  1753, 1898, 1940, 1900, 1939, 1898, 1899, 1828, 1913, 1789, 1940, 1940, 1944,
  1944, 1949, 1950, 1952, 1952, 1955, 1957, 1961, 1964, 1967, 1974, 1976, 1984,
  1982, 1994, 1994, 1996, 2004, 1998, 2003, 2000, 2010, 2006,
];

/**
 * Ground-state configurations that depart from the Madelung order, as the subshells
 * outside the preceding noble-gas core (NIST ASD / CRC Handbook).
 */
export const CONFIG_EXCEPTIONS: Readonly<Record<number, string>> = {
  24: "3d5 4s1",
  29: "3d10 4s1",
  41: "4d4 5s1",
  42: "4d5 5s1",
  44: "4d7 5s1",
  45: "4d8 5s1",
  46: "4d10",
  47: "4d10 5s1",
  57: "5d1 6s2",
  58: "4f1 5d1 6s2",
  64: "4f7 5d1 6s2",
  78: "4f14 5d9 6s1",
  79: "4f14 5d10 6s1",
  89: "6d1 7s2",
  90: "6d2 7s2",
  91: "5f2 6d1 7s2",
  92: "5f3 6d1 7s2",
  93: "5f4 6d1 7s2",
  96: "5f7 6d1 7s2",
  103: "5f14 7s2 7p1",
};

/** First element with no measured ground-state configuration (rutherfordium). */
export const FIRST_PREDICTED_CONFIG = 104;
